import axios from "axios";
import config from "./config/frontend.config";
import showToast from "./components/Toast";
import globalRequestManager from "./utils/globalRequestManager";

const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

// Flag to prevent multiple logout toasts
let isLoggingOut = false;

// Flag to prevent multiple session expired toast messages
let sessionExpiredToastShown = false;

// Function to reset logout flag (called on successful login)
export const resetLogoutFlag = () => {
  isLoggingOut = false;
  sessionExpiredToastShown = false; // Reset toast flag on login
};

// Function to check if logout is in progress
export const isLogoutInProgress = () => {
  return isLoggingOut;
};

const getToken = () => {
  const adminToken = localStorage.getItem("authorization");
  if (adminToken) return adminToken;
  const userToken = config.getAuthToken();
  if (userToken) return userToken;
  return null;
};

const getId = () => {
  const adminId = localStorage.getItem("id");
  if (adminId) return adminId;
  const userId = config.getUserId();
  if (userId) return userId;
  return null;
};

// Function to handle logout and clear all auth data
// Uses isLoggingOut flag to prevent multiple logout calls and duplicate toast messages
const handleLogout = (showToast = true) => {
  // Prevent multiple logout calls
  if (isLoggingOut) return;
  isLoggingOut = true;
  
  // Clear all authentication data
  // localStorage.removeItem("authorization");
  // localStorage.removeItem("id");
  // localStorage.removeItem("Token");
  // localStorage.removeItem("UserId");
  // localStorage.removeItem("UserData");
  // localStorage.removeItem("UserRole");

  localStorage.clear();
  
  // Clear any other auth-related data
  sessionStorage.clear();
  
  // Show logout message only if requested
  if (showToast) {
    showToast.error("Session expired. Please log in again.");
  }
  
  // Redirect to login page
  window.location.href = '/auth';
};

const AxiosInstance = axios.create({
  baseURL: BASE_URL,
});

AxiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    const id = getId();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (id) {
      config.headers["id"] = id;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Enhanced function to get specific error message for 401 errors
const get401ErrorMessage = (errorData) => {
  if (!errorData) return "Authentication failed. Please log in again.";
  
  const message = errorData.message || errorData.error || "";
  const lowerMessage = message.toLowerCase();
  
  // Token expired
  if (lowerMessage.includes("expired") || lowerMessage.includes("token expired")) {
    return "Your session has expired. Please log in again.";
  }
  
  // Token invalid
  if (lowerMessage.includes("invalid") || lowerMessage.includes("token invalid")) {
    return "Invalid authentication token. Please log in again.";
  }
  
  // Missing token or ID
  if (lowerMessage.includes("required") || lowerMessage.includes("authorization token and id")) {
    return "Authentication credentials are missing. Please log in again.";
  }
  
  // User not verified
  if (lowerMessage.includes("not verify") || lowerMessage.includes("user is not verify") || lowerMessage.includes("user verification failed")) {
    return "User verification failed. Please log in again.";
  }
  
  // ID mismatch
  if (lowerMessage.includes("id mismatch") || lowerMessage.includes("user is not verified")) {
    return "User session mismatch. Please log in again.";
  }
  
  // Invalid token format
  if (lowerMessage.includes("invalid token or id format")) {
    return "Invalid authentication format. Please log in again.";
  }
  
  // Invalid credentials
  if (lowerMessage.includes("invalid credentials") || lowerMessage.includes("invalid email and password")) {
    return "Invalid email and password. Please check your credentials.";
  }
  
  // User not found
  if (lowerMessage.includes("user not found")) {
    return "User account not found. Please check your credentials.";
  }
  
  // Account locked or disabled
  if (lowerMessage.includes("locked") || lowerMessage.includes("disabled") || lowerMessage.includes("suspended")) {
    return "Your account has been locked. Please contact support.";
  }
  
  // Default message
  return message || "Authentication failed. Please log in again.";
};

// Enhanced response interceptor to handle 401 errors and token issues
// Prevents multiple toast messages when session expires by using isLoggingOut flag
AxiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle 401 Unauthorized errors with enhanced messaging
      if (status === 401) {
        const errorMessage = get401ErrorMessage(data);
        
        // Check if this is a login attempt (by checking the request URL)
        const isLoginRequest = error.config?.url?.includes('/user/login') || 
                              error.config?.url?.includes('/auth/login');
        
        // Only logout and redirect for non-login requests
        // For login requests, just show the error message without redirecting
        if (!isLoginRequest && !isLoggingOut) {
          // Show error message only once, even if multiple 401 errors occur simultaneously
          if (!sessionExpiredToastShown) {
            sessionExpiredToastShown = true;
            showToast.error(errorMessage);
          }
          
          // Automatically logout the user for 401 errors (except login attempts)
          // Add a small delay to ensure the toast is shown
          setTimeout(() => {
            handleLogout(false); // Don't show duplicate toast
          }, 1000);
        }
        
        return Promise.reject(error);
      }
      
      // Handle 403 Forbidden errors
      if (status === 403) {
        const message = data?.message || data?.error || "Access denied. You don't have permission to perform this action.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Handle 404 Not Found errors
      else if (status === 404) {
        const message = data?.message || data?.error || "Resource not found.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Handle 409 Conflict errors
      else if (status === 409) {
        const message = data?.message || data?.error || "Conflict occurred. Please check your data and try again.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Handle 422 Validation errors
      else if (status === 422) {
        const message = data?.message || data?.error || "Validation error. Please check your input.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Handle 429 Rate limit errors
      else if (status === 429) {
        const message = data?.message || data?.error || "Too many requests. Please try again later.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Handle 500+ Server errors
      else if (status >= 500) {
        const message = data?.message || data?.error || "Server error. Please try again later.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Handle other client errors (400-499)
      else if (status >= 400 && status < 500) {
        const message = data?.message || data?.error || "Request failed. Please check your input and try again.";
        showToast.error(message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } else if (error.request) {
      // Network error - no response received
      showToast.error("Network error. Please check your internet connection and try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      // Other errors (like request cancellation, etc.)
      if (error.message && !error.message.includes('canceled')) {
        showToast.error("An unexpected error occurred. Please try again.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
    
    return Promise.reject(error);
  }
);

// Override the request method to use global request manager for deduplication
const originalRequest = AxiosInstance.request;
AxiosInstance.request = function(config) {
  // Extract request details
  const method = config.method || 'GET';
  const url = config.url;
  const params = config.params || {};
  const data = config.data || {};
  
  // Use global request manager to prevent duplicate calls
  return globalRequestManager.executeRequest(
    () => originalRequest.call(this, config),
    method,
    url,
    params,
    data
  );
};

// Override specific HTTP methods to ensure all requests go through deduplication
const originalGet = AxiosInstance.get;
const originalPost = AxiosInstance.post;
const originalPut = AxiosInstance.put;
const originalDelete = AxiosInstance.delete;

AxiosInstance.get = function(url, config = {}) {
  return globalRequestManager.executeRequest(
    () => originalGet.call(this, url, config),
    'GET',
    url,
    config.params || {},
    config.data || {}
  );
};

AxiosInstance.post = function(url, data, config = {}) {
  return globalRequestManager.executeRequest(
    () => originalPost.call(this, url, data, config),
    'POST',
    url,
    config.params || {},
    data || {}
  );
};

AxiosInstance.put = function(url, data, config = {}) {
  return globalRequestManager.executeRequest(
    () => originalPut.call(this, url, data, config),
    'PUT',
    url,
    config.params || {},
    data || {}
  );
};

AxiosInstance.delete = function(url, config = {}) {
  return globalRequestManager.executeRequest(
    () => originalDelete.call(this, url, config),
    'DELETE',
    url,
    config.params || {},
    config.data || {}
  );
};

export default AxiosInstance; 